import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useRef, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Store from "../../../utils/Store";

/**
 * Release ChatInput, which disables itself while a turn is outstanding.
 *
 * ChatInput sets `hasUserSentMessage` on send and then waits for someone to
 * clear it. On the CopilotKit path that someone is useConversationLogs, which
 * reacts to the websocket reply; this chat never goes near it, so without this
 * the box stays disabled from the first message until the window is reopened.
 *
 * A shared store flag rather than a prop because ChatInput reads it directly.
 * Passing `disabled` down would be cleaner, but ChatInput is shared with the
 * legacy chat and changing how it decides would move that too.
 */
function releaseChatInput() {
  Store.update(function (s) {
    s.hasUserSentMessage = false;
  });
}

/**
 * Conversation state for the orqestrator chat.
 *
 * Owns the message list, the in-flight request, and cancellation. The component
 * renders; this decides what there is to render.
 *
 * The network call arrives as the `transport` argument rather than being built
 * in, so this state machine never mentions orqestrator. See useOrqTransport for
 * the implementation that talks to it.
 */

/** A tool the agent invoked, and whether it has finished. */

/**
 * How a turn reaches the agent.
 *
 * Deliberately narrow: a prompt in, text deltas out, an AbortSignal to stop.
 * Nothing about SSE, AG-UI, cookies or JWTs appears here, so the transport can
 * change without this file knowing.
 */

/**
 * Shown in place of a reply when a turn fails.
 *
 * Deliberately says nothing about tokens, orqestrator or HTTP status. The
 * actionable detail goes to Datadog; a clinician needs to know whether to try
 * again, not which service failed.
 */
var USER_FACING_ERROR = "Sorry, I could not get a response just now. Please try again in a moment.";

/** Supplies the stored conversation, if there is one. Same narrow shape as
 *  `transport`: messages in, nothing about how they were fetched. */

export function useOrqChat(transport, loadHistory,
/**
 * Which conversation `loadHistory` will replay. When it changes the chat is
 * looking at a different thread, so what is on screen is replaced by that
 * thread's history.
 *
 * Needed because the chat is mounted app-wide and never remounts: without it,
 * history is fetched once at mount — before the patient has even loaded from
 * the store — and a later patient switch leaves the previous conversation on
 * screen. Opaque here on purpose; useOrqTransport decides what scopes a
 * thread.
 */
historyScope) {
  var _useState = useState([]),
    messages = _useState[0],
    setMessages = _useState[1];
  var _useState2 = useState(Boolean(loadHistory)),
    loadingHistory = _useState2[0],
    setLoadingHistory = _useState2[1];
  var _useState3 = useState("idle"),
    status = _useState3[0],
    setStatus = _useState3[1];
  var _useState4 = useState(),
    error = _useState4[0],
    setError = _useState4[1];
  var abortRef = useRef(null);
  // Ids come from a counter rather than messages.length, which repeats after a
  // clear and would give React duplicate keys.
  var seqRef = useRef(0);
  var nextId = function nextId(role) {
    return "".concat(role, "-").concat(seqRef.current += 1);
  };

  // Replay the stored conversation once, on mount. Prepended rather than
  // assigned, so a turn sent while history was still in flight is not lost.
  useEffect(function () {
    if (!loadHistory) return;
    var cancelled = false;
    // Clear first: a scope change means the messages on screen belong to the
    // conversation being left, and they must not survive into this one even if
    // the fetch fails or comes back empty.
    setMessages([]);
    setLoadingHistory(true);
    loadHistory().then(function (history) {
      if (cancelled || history.length === 0) return;
      setMessages(function (current) {
        return [].concat(_toConsumableArray(history.map(function (m, i) {
          return {
            id: "history-".concat(i),
            role: m.role,
            content: m.content
          };
        })), _toConsumableArray(current));
      });
    })["finally"](function () {
      if (!cancelled) setLoadingHistory(false);
    });
    return function () {
      cancelled = true;
    };
    // Keyed on the scope, not on `loadHistory`, whose identity changes on every
    // render and would refetch in a loop. Re-running for the same scope would
    // duplicate the replayed messages, which the clear above also guards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyScope]);

  // Abort anything in flight when the sidebar unmounts, so a closed chat cannot
  // keep streaming into state that no longer exists.
  useEffect(function () {
    return function () {
      var _abortRef$current;
      (_abortRef$current = abortRef.current) === null || _abortRef$current === void 0 || _abortRef$current.abort();
    };
  }, []);
  var stop = useCallback(function () {
    var _abortRef$current2;
    (_abortRef$current2 = abortRef.current) === null || _abortRef$current2 === void 0 || _abortRef$current2.abort();
    abortRef.current = null;
    setStatus("idle");
    releaseChatInput();
    setMessages(function (prev) {
      return prev.map(function (m) {
        return m.streaming ? _objectSpread(_objectSpread({}, m), {}, {
          streaming: false
        }) : m;
      });
    });
  }, []);
  var clear = useCallback(function () {
    var _abortRef$current3;
    (_abortRef$current3 = abortRef.current) === null || _abortRef$current3 === void 0 || _abortRef$current3.abort();
    abortRef.current = null;
    setMessages([]);
    setStatus("idle");
    releaseChatInput();
    setError(undefined);
  }, []);
  var send = useCallback(function (text) {
    var prompt = text.trim();
    // One turn at a time: the agent has no notion of concurrent runs, and a
    // second request would interleave deltas into the wrong bubble.
    if (!prompt || abortRef.current) return;
    var controller = new AbortController();
    abortRef.current = controller;
    setError(undefined);
    setStatus("streaming");

    // The assistant bubble is created empty and up front, so the UI has
    // something to stream into and the user sees a response forming.
    var replyId = nextId("a");
    setMessages(function (prev) {
      return [].concat(_toConsumableArray(prev), [{
        id: nextId("u"),
        role: "user",
        content: prompt
      }, {
        id: replyId,
        role: "assistant",
        content: "",
        streaming: true
      }]);
    });
    var appendDelta = function appendDelta(delta) {
      return setMessages(function (prev) {
        return prev.map(function (m) {
          return m.id === replyId ? _objectSpread(_objectSpread({}, m), {}, {
            content: m.content + delta
          }) : m;
        });
      });
    };

    // Tools are tracked by id rather than appended blindly: each arrives
    // twice (start, then end) and two can interleave.
    var noteTool = function noteTool(tool) {
      return setMessages(function (prev) {
        return prev.map(function (m) {
          var _m$tools;
          if (m.id !== replyId) return m;
          var tools = (_m$tools = m.tools) !== null && _m$tools !== void 0 ? _m$tools : [];
          var seen = tools.findIndex(function (t) {
            return t.id === tool.id;
          });
          return _objectSpread(_objectSpread({}, m), {}, {
            tools: seen === -1 ? [].concat(_toConsumableArray(tools), [tool]) :
            // Merged, not replaced: start carries the name, end the
            // args, result the output - each event knows only its part.
            tools.map(function (t, i) {
              return i === seen ? _objectSpread(_objectSpread({}, t), tool) : t;
            })
          });
        });
      });
    };
    transport(prompt, {
      onDelta: appendDelta,
      onTool: noteTool
    }, controller.signal).then(function () {
      if (controller.signal.aborted) return;
      setStatus("idle");
      releaseChatInput();
    })["catch"](function (err) {
      // An abort is a user action, not a failure worth reporting.
      if (controller.signal.aborted) return;

      // The real reason goes to Datadog, not to the person using the app.
      // "Could not mint orqestrator token" means nothing to a clinician and
      // reads as though the assistant said it.
      datadogRum.addError(err instanceof Error ? err : new Error(String(err)), {
        scope: "orq-chat"
      });
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
      // A failed turn must release the input too, or one error locks the
      // chat for the rest of the session.
      releaseChatInput();
      setMessages(function (prev) {
        return prev.map(function (m) {
          return m.id === replyId ? _objectSpread(_objectSpread({}, m), {}, {
            content: m.content || USER_FACING_ERROR,
            streaming: false,
            failed: true
          }) : m;
        });
      });
    })["finally"](function () {
      // Clearing the ref is what re-opens the input, so it must happen on
      // every path including abort.
      if (abortRef.current === controller) abortRef.current = null;
      setMessages(function (prev) {
        return prev.map(function (m) {
          return m.id === replyId ? _objectSpread(_objectSpread({}, m), {}, {
            streaming: false
          }) : m;
        });
      });
    });
  }, [transport]);
  return {
    messages: messages,
    loadingHistory: loadingHistory,
    status: status,
    error: error,
    send: send,
    stop: stop,
    clear: clear
  };
}